import { useSelector } from "react-redux";
import { useEffect, useState } from "react";
import moment from "jalali-moment";
import { useLocation, useParams } from "react-router-dom";
import DownloadChats from "./DownloadChats";
import { ProgressBar } from "react-loader-spinner";
import { Menu } from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/20/solid";
import { FaSearch } from "react-icons/fa";
import DateFilterBox from "units/admin/transactions/components/DateFilterBox";
import { endConversation } from "../services/chatService";
import { confirmEndToast, showToast } from "toast/toastHelper";
import {
  MessagesSquare, Filter, X, ChevronRight, ChevronLeft,
  CheckCircle2, Clock, XCircle, Trash2, Flag,
} from "lucide-react";
import { useTranslation } from "react-i18next";

const ChatConversion = ({
  conversations, onConversationClick, title, selectedMenu,
  selectedItem, senderPhone, loading,
}) => {
  const { t } = useTranslation(["user"]);

  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isDemoDownloading, setIsDemoDownloading] = useState(false);
  const [isShow, setIsShow] = useState(false);
  const [dateFilter, setDateFilter] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [selectedMinDate, setSelectedMinDate] = useState(null);
  const [selectedMaxDate, setSelectedMaxDate] = useState(null);
  const [globalFilter, setGlobalFilter] = useState("");
  const accessToken = useSelector((state) => state.auth.accessToken);
  const { botType } = useParams();
  const location = useLocation();
  const isSupportPage = location.pathname.includes("supportBot");
  const user = useSelector((state) => state.userInfo);
  const userType = user?.userType;

  /* ================================================================
   *  LOGIC — دست‌نخورده
   * ================================================================ */
  const conversationsFiltered = conversations.reduce(
    (acc, c) => {
      if (c.withGPT) acc.withGPTConversations.push(c);
      else acc.withoutGPTConversations.push(c);
      return acc;
    },
    { withGPTConversations: [], withoutGPTConversations: [] }
  );

  const { withGPTConversations, withoutGPTConversations } = conversationsFiltered;

  const conversationsFinal =
    selectedMenu === "supportBot"
      ? selectedItem === "RayaBot"
        ? withGPTConversations
        : withoutGPTConversations
      : selectedMenu === "adminSupportBot"
        ? withoutGPTConversations
        : "";

  useEffect(() => {
    const hasNonGPT = conversationsFinal.some((c) => !c?.withGPT);
    setIsShow(hasNonGPT);
  }, [conversationsFinal, selectedMenu]);

  const sender = (conversation) => {
    if (selectedMenu === "supportBot") {
      return selectedItem === "RayaBot"
        ? `${conversation?.sender?.countryCode.replace(/^\+/, "")}${conversation?.sender?.phone}+`
        : t("chatConversion.admin");
    } else if (selectedMenu === "adminSupportBot") {
      return `${conversation?.sender?.firstName} ${conversation?.sender?.lastName}`;
    }
    return "-----";
  };

  const getChatStatus = (conversation) => {
    const status = conversation?.status;
    if (status === "deleted_by_user")
      return { bgClass: "bg-rose-500/10 hover:bg-rose-500/15", textClass: "text-rose-700 dark:!text-rose-300", isDeleted: true };
    if (status === "ended")
      return { bgClass: "bg-emerald-500/10 hover:bg-emerald-500/15", textClass: "text-emerald-700 dark:!text-emerald-300", isDeleted: false };
    if (status === "question")
      return { bgClass: "bg-amber-500/10 hover:bg-amber-500/15", textClass: "text-amber-800 dark:!text-amber-200", isDeleted: false };
    return { bgClass: "", textClass: "", isDeleted: false };
  };

  const handleEndConversation = async (conversationId) => {
    try {
      await endConversation({ accessToken, roomId: conversationId });
      showToast(t("chatConversion.toasts.endSuccess"), "success");
      window.location.reload();
    } catch (error) {
      switch (error.statusCode) {
        case 404: showToast(t("chatConversion.toasts.notFound"), "error"); break;
        case 403: showToast(t("chatConversion.toasts.noAccess"), "error"); break;
        case 409: showToast(t("chatConversion.toasts.alreadyEnded"), "warning"); break;
        default: showToast(error.message || t("chatConversion.toasts.unknownError"), "error");
      }
    }
  };

  const startIndex = (currentPage - 1) * rowsPerPage;

  const getTimestamp = (date) => {
    if (!date) return null;
    return moment(`${date.year}/${date.month}/${date.day}`, "jYYYY/jM/jD").toDate().getTime();
  };

  const filteredConversations = conversationsFinal.filter((conversation) => {
    const conversationDate = new Date(conversation.updatedAt).getTime();
    if (selectedMinDate) {
      const min = getTimestamp(selectedMinDate);
      if (conversationDate < min) return false;
    }
    if (selectedMaxDate) {
      const max = moment(
        `${selectedMaxDate.year}/${selectedMaxDate.month}/${selectedMaxDate.day}`,
        "jYYYY/jM/jD"
      ).endOf("day").toDate().getTime();
      if (conversationDate > max) return false;
    }
    if (selectedStatus !== "all") {
      switch (selectedStatus) {
        case "answered": if (conversation.status !== "answer") return false; break;
        case "waiting": if (conversation.status !== "question") return false; break;
        case "ended": if (conversation.status !== "ended") return false; break;
        default: break;
      }
    }
    return true;
  });

  const resetFilters = () => {
    setSelectedStatus("all");
    setSelectedMinDate(null);
    setSelectedMaxDate(null);
    setCurrentPage(1);
    setGlobalFilter("");
  };

  const searchedConversations = filteredConversations.filter((conversation) => {
    if (!globalFilter.trim()) return true;
    const search = globalFilter.toLowerCase().trim();
    const conversationName = (conversation.name || "").toLowerCase();
    let senderName = "";
    if (selectedMenu === "supportBot") {
      senderName = `${conversation?.sender?.countryCode?.replace(/^\+/, "") || ""}${conversation?.sender?.phone || ""}`.toLowerCase();
    } else if (selectedMenu === "adminSupportBot") {
      senderName = `${conversation?.sender?.firstName || ""} ${conversation?.sender?.lastName || ""}`.toLowerCase();
    }
    return conversationName.includes(search) || senderName.includes(search);
  });

  const totalPages = Math.ceil(searchedConversations.length / rowsPerPage);

  const selectedConversations = searchedConversations
    .filter((c) => c.updatedAt)
    .toSorted((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt))
    .slice(startIndex, startIndex + rowsPerPage);

  const hasActiveFilters =
    globalFilter ||
    selectedStatus !== "all" ||
    selectedMinDate ||
    selectedMaxDate;

  /* ================================================================
   *  LOADING
   * ================================================================ */
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 h-[60vh]">
        <ProgressBar
          visible={true}
          height="70"
          width="70"
          barColor="#14B8A6"
          borderColor="#FCD34D"
          ariaLabel="progress-bar-loading"
        />
        <p className="text-sm text-slate-500 dark:!text-slate-400 animate-pulse">
          {t("chatConversion.loading")}
        </p>
      </div>
    );
  }

  /* ================================================================
   *  RENDER
   * ================================================================ */
  return (
    <div className="col-span-12 xl:col-span-6 min-w-0">
      {/* ⚠️ min-w-0 روی والدها حیاتی است تا overflow-x-auto کار کنه */}

      <div
        className="
          relative w-full min-w-0
          rounded-2xl
          bg-white/60 dark:!bg-slate-900/60
          backdrop-blur-xl
          border border-teal-500/15 dark:!border-teal-400/15
          shadow-[0_10px_40px_-20px_rgba(15,118,110,0.4)]
          dark:!shadow-[0_10px_40px_-20px_rgba(0,0,0,0.8)]
        "
      >
        {/* خط طلایی بالا — داخل یه wrapper مخصوص با overflow-hidden */}
        <div className="pointer-events-none absolute top-0 inset-x-0 h-[2px] rounded-t-2xl overflow-hidden">
          <div className="h-full bg-gradient-to-l from-transparent via-amber-400/70 to-transparent" />
        </div>

        {/* ============================================================
         *  Header: جستجو + فیلترها (چیدمان تمیز)
         * ============================================================ */}
        <div className="relative p-3 sm:p-4 space-y-3">

          {/* ردیف ۱: جستجو + دکمه حذف فیلترها */}
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="relative flex-1 min-w-0 group">
              <FaSearch className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 dark:!text-slate-500 text-sm z-10 pointer-events-none" />
              <input
                id="search"
                value={globalFilter}
                onChange={(e) => {
                  setGlobalFilter(e.target.value);
                  setCurrentPage(1);
                }}
                placeholder={t("chatConversion.searchPlaceholder")}
                className="
                  w-full pr-9 pl-3 py-2.5 rounded-xl text-sm
                  bg-white/70 dark:!bg-slate-800/60
                  text-slate-700 dark:!text-slate-200
                  placeholder:text-slate-400 dark:!placeholder:text-slate-500
                  border border-teal-500/20 dark:!border-teal-400/20
                  focus:outline-none
                  focus:ring-2 focus:ring-amber-300/60
                  focus:border-amber-300/60
                  transition-all duration-200
                "
              />
              {globalFilter && (
                <button
                  type="button"
                  onClick={() => {
                    setGlobalFilter("");
                    setCurrentPage(1);
                  }}
                  aria-label={t("chatConversion.clearSearch")}
                  className="absolute left-2 top-1/2 -translate-y-1/2 h-6 w-6 flex items-center justify-center rounded-md text-slate-400 hover:text-rose-500 hover:bg-rose-500/10 transition-colors"
                >
                  <X size={13} />
                </button>
              )}
            </div>

            <button
              onClick={resetFilters}
              disabled={!hasActiveFilters}
              title={t("chatConversion.clearFiltersTitle")}
              className={`
                shrink-0 flex items-center justify-center gap-1.5
                h-10 px-3 sm:px-4 rounded-xl
                text-xs font-bold
                transition-all duration-300
                whitespace-nowrap
                ${hasActiveFilters
                  ? "text-amber-900 dark:!text-amber-100 bg-gradient-to-l from-amber-300/70 to-amber-400/40 border border-amber-300/50 hover:from-amber-300 hover:to-amber-400 hover:scale-[1.03] active:scale-95 shadow-[0_4px_16px_-6px_rgba(251,191,36,0.6)]"
                  : "text-slate-400 dark:!text-slate-600 bg-slate-100/50 dark:!bg-slate-800/40 border border-slate-200/50 dark:!border-slate-700/50 cursor-not-allowed"
                }
              `}
            >
              <X size={13} />
              <span className="hidden sm:inline">{t("chatConversion.clearFilters")}</span>
            </button>
          </div>

          {/* ردیف ۲: فیلترها + دانلود */}
          <div className="flex flex-wrap items-center gap-2">

            {isSupportPage && (
              <Menu as="div" className="relative inline-block text-right">
                <Menu.Button
                  className="
                    inline-flex items-center gap-2
                    rounded-xl
                    px-3 py-2.5
                    text-xs sm:text-sm font-semibold
                    text-slate-700 dark:!text-slate-200
                    bg-white/70 dark:!bg-slate-800/60
                    border border-teal-500/20 dark:!border-teal-400/20
                    backdrop-blur-md
                    transition-all duration-200
                    hover:border-amber-300/60
                    hover:shadow-[0_4px_16px_-6px_rgba(251,191,36,0.4)]
                    whitespace-nowrap
                  "
                >
                  <Filter size={13} className="text-teal-600/70 dark:!text-teal-300/70 shrink-0" />
                  <span className="truncate max-w-[140px]">
                    {t(`chatConversion.filters.${selectedStatus}`)}
                  </span>
                  <ChevronDownIcon className="-ml-1 size-4 text-slate-400 shrink-0" />
                </Menu.Button>

                <Menu.Items
                  className="
                    absolute right-0 z-30 mt-2 min-w-[180px]
                    origin-top-right
                    rounded-xl overflow-hidden
                    bg-white/95 dark:!bg-slate-900/95
                    backdrop-blur-xl
                    shadow-[0_20px_60px_-20px_rgba(15,118,110,0.5)]
                    dark:!shadow-[0_20px_60px_-20px_rgba(0,0,0,0.9)]
                    border border-teal-500/20 dark:!border-teal-400/20
                    focus:outline-none
                  "
                >
                  <div className="h-[2px] bg-gradient-to-l from-transparent via-amber-400/70 to-transparent" />
                  <div className="py-1">
                    {[
                      { key: "answered", icon: CheckCircle2, tone: "text-emerald-500" },
                      { key: "waiting", icon: Clock, tone: "text-amber-500" },
                      { key: "ended", icon: Flag, tone: "text-teal-500" },
                    ].map(({ key, icon: Icon, tone }) => (
                      <Menu.Item key={key}>
                        {({ active }) => (
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedStatus(key);
                              setCurrentPage(1);
                            }}
                            className={`
                              flex w-full items-center justify-end gap-2
                              px-4 py-2.5 text-xs sm:text-sm font-medium
                              text-slate-700 dark:!text-slate-200
                              transition-colors duration-200
                              ${active ? "bg-amber-300/20 dark:!bg-amber-400/10" : ""}
                              ${selectedStatus === key ? "bg-teal-500/10 dark:!bg-teal-400/10" : ""}
                            `}
                          >
                            <span>{t(`chatConversion.filters.${key}`)}</span>
                            <Icon size={14} className={tone} />
                          </button>
                        )}
                      </Menu.Item>
                    ))}
                  </div>
                </Menu.Items>
              </Menu>
            )}

            {/* <div className="flex-shrink-0">
              <DateFilterBox
                selectedMinDate={selectedMinDate}
                setSelectedMinDate={setSelectedMinDate}
                selectedMaxDate={selectedMaxDate}
                setSelectedMaxDate={setSelectedMaxDate}
                onClearDates={() => {
                  setSelectedMinDate(null);
                  setSelectedMaxDate(null);
                }}
              />
            </div>

            <div className="flex-shrink-0">
              <DownloadChats
                conversations={conversations}
                onDateRangeChange={(range) => setDateFilter(range)}
                onClearDateRange={() => setDateFilter(null)}
              />
            </div> */}
          </div>
        </div>

        {/* ============================================================
         *  Table — با اسکرول افقی کار‌کردنی
         * ============================================================ */}
        <div className="relative border-t border-teal-500/10 dark:!border-teal-400/10">
          <div className="overflow-auto w-full max-h-[60vh]">
            <table className="w-full min-w-[760px] text-sm text-right">
              <thead className="sticky top-0 z-10 bg-white/95 dark:!bg-slate-900/95 backdrop-blur-xl">
                <tr className="border-b border-teal-500/15 dark:!border-teal-400/15 bg-gradient-to-l from-teal-500/5 via-transparent to-amber-300/5">
                  <th className="px-4 sm:px-6 py-3.5 text-xs font-bold text-slate-500 dark:!text-slate-400 tracking-wide whitespace-nowrap">
                    <span className="flex items-center gap-1.5">
                      <span className="h-1 w-1 rounded-full bg-amber-400" />
                      {t("chatConversion.columns.row")}
                    </span>
                  </th>
                  <th className="px-4 sm:px-6 py-3.5 text-xs font-bold text-slate-500 dark:!text-slate-400 tracking-wide whitespace-nowrap">
                    {t("chatConversion.columns.conversationName")}
                  </th>
                  {botType === "RayaBot" && (
                    <th className="px-4 sm:px-6 py-3.5 text-xs font-bold text-slate-500 dark:!text-slate-400 tracking-wide whitespace-nowrap">
                      {t("chatConversion.columns.popularity")}
                    </th>
                  )}
                  <th className="px-4 sm:px-6 py-3.5 text-xs font-bold text-slate-500 dark:!text-slate-400 tracking-wide whitespace-nowrap">
                    {t("chatConversion.columns.sender")}
                  </th>
                  {isShow && (
                    <th className="px-4 sm:px-6 py-3.5 text-xs font-bold text-slate-500 dark:!text-slate-400 tracking-wide whitespace-nowrap">
                      {t("chatConversion.columns.status")}
                    </th>
                  )}
                  <th className="px-4 sm:px-6 py-3.5 text-xs font-bold text-slate-500 dark:!text-slate-400 tracking-wide whitespace-nowrap">
                    {t("chatConversion.columns.date")}
                  </th>
                  {isSupportPage && (
                    <th className="px-4 sm:px-6 py-3.5 text-xs font-bold text-slate-500 dark:!text-slate-400 tracking-wide whitespace-nowrap">
                      {t("chatConversion.columns.actions")}
                    </th>
                  )}
                </tr>
              </thead>

              <tbody>
                {selectedConversations.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="px-4 py-12 text-center">
                      <div className="flex flex-col items-center gap-2 text-slate-400 dark:!text-slate-500">
                        <div className="h-12 w-12 rounded-full border-2 border-dashed border-teal-400/40 flex items-center justify-center">
                          <MessagesSquare size={20} className="animate-pulse text-teal-400/70" />
                        </div>
                        <p className="text-sm animate-pulse">
                          {t("chatConversion.noConversations")}
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  selectedConversations.map((conversation, index) => {
                    let likedMsgs = 0, dislikedMsgs = 0, nullMsgs = 0, totalResponses = 0;

                    if (botType === "RayaBot") {
                      conversation.messages.map((x) => {
                        if (!x.responseId) {
                          totalResponses += 1;
                          switch (x.likeStatus) {
                            case "like": likedMsgs += 1; break;
                            case "dislike": dislikedMsgs += 1; break;
                            default: nullMsgs += 1; break;
                          }
                        }
                        return x;
                      });
                    }

                    const chatStatus = getChatStatus(conversation);

                    return (
                      <tr
                        key={index}
                        onClick={() => onConversationClick(conversation)}
                        className={`
                          group cursor-pointer
                          border-b border-teal-500/8 dark:!border-teal-400/8
                          transition-all duration-200
                          ${chatStatus.bgClass || "hover:bg-teal-500/5 dark:!hover:bg-teal-400/5"}
                          ${chatStatus.textClass}
                        `}
                      >
                        <td
                          className="px-4 sm:px-6 py-3.5 dark:!text-zinc-100 tabular-nums text-xs font-semibold whitespace-nowrap"
                          title={conversation.id}
                        >
                          {searchedConversations.length - (startIndex + index)}
                        </td>

                        <td className="px-4 sm:px-6 py-3.5 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <span className="h-1.5 w-1.5 rounded-full bg-gradient-to-br from-amber-300 to-teal-400 shadow-[0_0_6px_rgba(251,191,36,0.6)] shrink-0" />
                            <span className="font-bold text-slate-800 dark:!text-slate-100 truncate max-w-[180px]">
                              {conversation.name}
                            </span>
                          </div>
                        </td>

                        {botType === "RayaBot" && (
                          <td
                            className="px-4 sm:px-6 py-3.5 whitespace-nowrap"
                            title={`${likedMsgs} لایک - ${dislikedMsgs} دیسلایک - خنثی ${nullMsgs}`}
                          >
                            {(() => {
                              const value =
                                dislikedMsgs > 0
                                  ? (((likedMsgs + nullMsgs - dislikedMsgs) / totalResponses) * 100).toFixed(0)
                                  : likedMsgs <= 0
                                    ? 0
                                    : ((likedMsgs / totalResponses) * 100).toFixed(0);

                              const tone =
                                value >= 70 ? "from-emerald-300 to-teal-500 text-emerald-800 dark:!text-emerald-100 border-emerald-400/40" :
                                  value >= 40 ? "from-amber-300 to-amber-500 text-amber-900 border-amber-400/50" :
                                    "from-rose-300 to-rose-500 text-rose-900 dark:!text-rose-100 border-rose-400/50";

                              return (
                                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] font-extrabold tabular-nums bg-gradient-to-l ${tone} border shadow-sm`}>
                                  {value}%
                                </span>
                              );
                            })()}
                          </td>
                        )}

                        <td className="px-4 sm:px-6 py-3.5 whitespace-nowrap font-medium text-slate-700 dark:!text-slate-200">
                          {sender(conversation)}
                        </td>

                        {isShow && (
                          <td className="px-4 sm:px-6 py-3.5 whitespace-nowrap">
                            {(() => {
                              const s = conversation.status;
                              if (s === "answer")
                                return <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] font-bold bg-emerald-500/15 text-emerald-700 dark:!text-emerald-300 border border-emerald-500/30"><CheckCircle2 size={11} />{t("chatConversion.status.answer")}</span>;
                              if (s === "question")
                                return <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] font-bold bg-amber-500/15 text-amber-800 dark:!text-amber-200 border border-amber-500/30"><Clock size={11} />{t("chatConversion.status.question")}</span>;
                              if (s === "ended")
                                return <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] font-bold bg-teal-500/15 text-teal-700 dark:!text-teal-300 border border-teal-500/30"><Flag size={11} />{t("chatConversion.status.ended")}</span>;
                              if (s === "deleted_by_user")
                                return <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] font-bold bg-rose-500/15 text-rose-700 dark:!text-rose-300 border border-rose-500/30"><Trash2 size={11} />{t("chatConversion.status.deleted_by_user")}</span>;
                              return <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] font-bold bg-slate-500/15 text-slate-600 dark:!text-slate-400 border border-slate-500/30"><XCircle size={11} />{t("chatConversion.status.noMessage")}</span>;
                            })()}
                          </td>
                        )}

                        <td className="px-4 sm:px-6 py-3.5 whitespace-nowrap text-xs tabular-nums text-slate-600 dark:!text-slate-300">
                          {moment(conversation.updatedAt + "Z")
                            .utcOffset(3.5 * 60)
                            .format("jYYYY/jMM/jDD HH:mm")}
                        </td>

                        {isSupportPage && (
                          <td className="px-4 sm:px-6 py-3.5 whitespace-nowrap">
                            {conversation.status !== "ended" && conversation.status !== "deleted_by_user" ? (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  confirmEndToast(() => handleEndConversation(conversation.id));
                                }}
                                className="
                                  group/end inline-flex items-center gap-1.5
                                  px-3 py-1.5 rounded-lg
                                  text-[11px] font-bold
                                  text-rose-700 dark:!text-rose-300
                                  bg-rose-500/10 hover:bg-rose-500/20
                                  border border-rose-400/40 hover:border-rose-400/70
                                  transition-all duration-200
                                  hover:shadow-[0_4px_16px_-6px_rgba(244,63,94,0.6)]
                                  hover:scale-[1.03] active:scale-95
                                "
                              >
                                <Flag size={11} />
                                {t("chatConversion.endConversation")}
                              </button>
                            ) : (
                              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-medium text-slate-400 dark:!text-slate-500 bg-slate-100/60 dark:!bg-slate-800/40 border border-slate-200/60 dark:!border-slate-700/40">
                                {conversation.status === "deleted_by_user"
                                  ? t("chatConversion.status.deleted_by_user")
                                  : t("chatConversion.status.ended")}
                              </span>
                            )}
                          </td>
                        )}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* ============================================================
         *  Pagination
         * ============================================================ */}
        <div className="relative p-3 sm:p-4 border-t border-teal-500/10 dark:!border-teal-400/10 flex flex-col sm:flex-row items-center justify-between gap-3">

          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="
                group flex items-center gap-1.5
                px-3 py-2 rounded-xl
                text-xs font-bold
                text-teal-700 dark:!text-amber-200
                bg-white/60 dark:!bg-white/5
                border border-teal-500/20 dark:!border-teal-400/20
                backdrop-blur-md
                transition-all duration-300
                hover:border-amber-300/60 hover:bg-amber-300/10
                hover:shadow-[0_4px_16px_-6px_rgba(251,191,36,0.5)]
                hover:scale-[1.03] active:scale-95
                disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100
              "
            >
              <ChevronRight size={14} />
              <span className="hidden sm:inline">{t("chatConversion.pagination.prev")}</span>
              <span className="sm:hidden">{t("chatConversion.pagination.prevShort")}</span>
            </button>

            <div className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-white/60 dark:!bg-white/5 border border-teal-500/20 dark:!border-teal-400/20 backdrop-blur-md">
              <span className="text-[11px] text-slate-500 dark:!text-slate-400">
                {t("chatConversion.pagination.page")}
              </span>
              <span className="text-xs font-extrabold text-amber-600 dark:!text-amber-300 tabular-nums">
                {currentPage}
              </span>
              <span className="text-[11px] text-slate-400 dark:!text-slate-500">
                {t("chatConversion.pagination.of")}
              </span>
              <span className="text-xs font-bold text-teal-700 dark:!text-teal-300 tabular-nums">
                {totalPages || 1}
              </span>
            </div>

            <button
              onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages || totalPages === 0}
              className="
                group flex items-center gap-1.5
                px-3 py-2 rounded-xl
                text-xs font-bold
                text-teal-700 dark:!text-amber-200
                bg-white/60 dark:!bg-white/5
                border border-teal-500/20 dark:!border-teal-400/20
                backdrop-blur-md
                transition-all duration-300
                hover:border-amber-300/60 hover:bg-amber-300/10
                hover:shadow-[0_4px_16px_-6px_rgba(251,191,36,0.5)]
                hover:scale-[1.03] active:scale-95
                disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100
              "
            >
              <span className="hidden sm:inline">{t("chatConversion.pagination.next")}</span>
              <span className="sm:hidden">{t("chatConversion.pagination.nextShort")}</span>
              <ChevronLeft size={14} />
            </button>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-[11px] text-slate-500 dark:!text-slate-400 whitespace-nowrap">
              {t("chatConversion.pagination.rowsPerPage")}
            </span>
            <select
              value={rowsPerPage}
              onChange={(e) => {
                setRowsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="
                px-2 py-1.5 rounded-lg
                text-xs font-bold
                text-slate-700 dark:!text-slate-200
                bg-white/60 dark:!bg-slate-800/60
                border border-teal-500/20 dark:!border-teal-400/20
                focus:outline-none focus:ring-2 focus:ring-amber-300/60
                cursor-pointer
                tabular-nums
              "
            >
              {[5, 10, 20, 50].map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatConversion;